// ============================================
// Value of Advice - Research Data Constants
// ============================================

/**
 * Vanguard Adviser's Alpha Research Data
 * Source: Vanguard "Putting a value on your value" (2024)
 * Total potential value add: ~3% per annum
 */
export const VANGUARD_ALPHA = {
  totalPotential: 0.03,
  components: [
    {
      id: 'behavioural_coaching',
      name: 'Behavioural Coaching',
      description: 'Helping clients separate emotions from investment decisions',
      valueBps: 200,
      valuePercent: 2.0,
      color: '#ec4899',
    },
    {
      id: 'withdrawal_strategy',
      name: 'Withdrawal Strategy',
      description: 'Strategic withdrawal sequencing to minimise tax',
      valueBps: 120,
      valuePercent: 1.2,
      color: '#8b5cf6',
    },
    {
      id: 'asset_location',
      name: 'Asset Location',
      description: 'Strategic placement in tax-advantaged accounts',
      valueBps: 68,
      valuePercent: 0.68,
      color: '#06b6d4',
    },
    {
      id: 'cost_effective',
      name: 'Cost-Effective Implementation',
      description: 'Using low-cost investment vehicles',
      valueBps: 38,
      valuePercent: 0.38,
      color: '#22c55e',
    },
    {
      id: 'rebalancing',
      name: 'Rebalancing',
      description: 'Disciplined portfolio rebalancing',
      valueBps: 22,
      valuePercent: 0.22,
      color: '#f59e0b',
    },
    {
      id: 'asset_allocation',
      name: 'Suitable Asset Allocation',
      description: 'Diversified funds matched to goals',
      valueBps: 30,
      valuePercent: 0.30,
      color: '#3b82f6',
    },
    {
      id: 'total_return',
      name: 'Total-Return Investing',
      description: 'Focusing on total returns',
      valueBps: 22,
      valuePercent: 0.22,
      color: '#10b981',
    },
  ],
} as const

export type VanguardComponent = (typeof VANGUARD_ALPHA.components)[number]

/**
 * ILC/Royal London Research Data
 * Source: ILC "What It's Worth" Study (2019), Royal London (2024)
 */
export const ILC_WEALTH_UPLIFT = {
  overallUplift: 47706,
  pensionWealth: 31000,
  nonPensionWealth: 16706,
  ongoingAdviceBonus: 0.5,
  insights: [
    'Proportionate impact is greater for those of more modest means',
    'Those with ongoing advice had nearly 50% higher pension wealth',
    'Advised clients are more likely to save regularly',
    'Advised clients are more likely to invest in equity markets',
  ],
} as const

/**
 * Default market return assumptions by risk profile
 */
export const PROJECTION_DEFAULTS = {
  marketReturn: {
    cautious: 0.04,
    balanced: 0.06,
    adventurous: 0.08,
  },
} as const

export type RiskProfile = keyof typeof PROJECTION_DEFAULTS.marketReturn

/**
 * Research sources for citations
 */
export const RESEARCH_SOURCES = [
  {
    name: 'Vanguard Adviser\'s Alpha',
    year: 2024,
    description: 'Quantifies adviser value at approximately 3% per annum',
  },
  {
    name: 'ILC "What It\'s Worth" Study',
    year: 2019,
    description: 'Found £47,706 average wealth uplift over 10 years',
  },
  {
    name: 'Royal London Research',
    year: 2024,
    description: 'Showed 50% higher pension wealth with ongoing advice',
  },
] as const

/**
 * Access codes for the calculator (optional gating)
 */
export const VALID_ACCESS_CODES = ['WEALTHSPACE2026', 'ADVISER2026', 'DEMO2026']
